/* "Report a problem or idea" (designs/issue-tracker.md IT9): the masthead
   button on every page, and the short form it opens as a modal side drawer.
   The page the person was on travels as context.view, so they never have to
   describe it. Filing goes to POST /api/issues — the same route Friday's
   connector calls — and the server stamps who filed it and the path. */
import { useRef, useState, type JSX } from "react";
import { createPortal } from "react-dom";
import { ISSUE_LIMITS, type IssueKind, type IssueView } from "../../../shared/issues";
import { asJson } from "../intake/api";
import { useAppState } from "./store";
import { currentSection } from "./sections";
import { useModal } from "./useModal";

const fileIssue = async (body: object): Promise<IssueView> =>
  asJson(
    await fetch("/api/issues", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  );

const orNull = (s: string): string | null => (s.trim() === "" ? null : s);

export function ReportIssueButton(): JSX.Element | null {
  const { store } = useAppState();
  if (!store.on("issues")) return null;
  return (
    <button className="report-btn" onClick={() => store.openReport()}>
      Report a problem or idea
    </button>
  );
}

/* mounted only while open, so the form starts empty every time and the modal
   behaviour (useModal) begins and ends with it */
export function ReportIssue(): JSX.Element | null {
  const { state } = useAppState();
  return state.reportOpen ? <ReportIssueDialog /> : null;
}

function ReportIssueDialog(): JSX.Element {
  const { state, store } = useAppState();
  const aside = useRef<HTMLElement>(null);
  const [kind, setKind] = useState<IssueKind>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filed, setFiled] = useState(false);
  const close = (): void => store.closeReport();
  useModal(aside, close);

  /* the area is named only when it is a tab of the CURRENT section — a tab
     left over from a record visited earlier is not where the person is
     (Copilot on #142) */
  const section = currentSection(store);
  const area = section && state.area && section.tabs.some((t) => t.label === state.area) ? state.area : null;
  const view = section ? (area ? `${section.label} / ${area}` : section.label) : null;

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fileIssue({
        kind,
        title,
        description,
        steps: kind === "bug" ? orNull(steps) : null,
        expected: kind === "bug" ? orNull(expected) : null,
        context: { view },
      });
      setFiled(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const seeMine = (): void => {
    close();
    store.selectNav("issues");
  };

  return createPortal(
    <div className="modal-root">
      <div className="side-drawer-backdrop" onClick={close} />
      <aside
        className="side-drawer open report-issue"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-issue-title"
        ref={aside}
        tabIndex={-1}
      >
        <h2 id="report-issue-title">Report a problem or idea</h2>
        {filed ? (
          <>
            <p className="report-done">
              Filed. An Admin will look at it, and you can follow what happens to it under My issues.
            </p>
            <div className="form-actions">
              <button className="btn primary" onClick={seeMine}>
                See My issues
              </button>
              <button className="btn ghost" onClick={close}>
                Close
              </button>
            </div>
          </>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <p className="report-hint">
              For Hermes itself: something broken or confusing, or something you wish it did. An
              engineering effort for the robots belongs in Intake instead.
            </p>
            <fieldset className="report-kind">
              <legend className="field-label">What is it?</legend>
              <label>
                <input type="radio" name="report-kind" checked={kind === "bug"} onChange={() => setKind("bug")} /> Bug
              </label>
              <label>
                <input
                  type="radio"
                  name="report-kind"
                  checked={kind === "feature"}
                  onChange={() => setKind("feature")}
                />{" "}
                Feature request
              </label>
            </fieldset>
            <label className="field">
              <span className="field-label">Title</span>
              <input
                id="report-title"
                type="text"
                maxLength={ISSUE_LIMITS.title}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">{kind === "bug" ? "What happened?" : "What would you like, and why?"}</span>
              <textarea
                id="report-description"
                rows={4}
                maxLength={ISSUE_LIMITS.text}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            {kind === "bug" && (
              <>
                <label className="field">
                  <span className="field-label">How to make it happen again (optional)</span>
                  <textarea
                    id="report-steps"
                    rows={3}
                    maxLength={ISSUE_LIMITS.text}
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                  />
                </label>
                <label className="field">
                  <span className="field-label">What you expected instead (optional)</span>
                  <textarea
                    id="report-expected"
                    rows={2}
                    maxLength={ISSUE_LIMITS.text}
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                  />
                </label>
              </>
            )}
            {view && <p className="report-hint">Hermes records that you were on {view}.</p>}
            <p className="report-hint">
              Don't include salaries, performance information, or Restricted content. Hermes Admins
              read every report.
            </p>
            <p className="form-error" role="alert">
              {error ?? ""}
            </p>
            <div className="form-actions">
              <button className="btn primary" type="submit" disabled={busy}>
                Submit report
              </button>
              <button className="btn ghost" type="button" onClick={close} disabled={busy}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </aside>
    </div>,
    document.body,
  );
}
